import { SelectionModel } from '@angular/cdk/collections';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import {
  AutocompleteOption,
  GroupedAutocompleteOptions,
} from '../../../shared/dto/grouped-autocomplete-options.model';
import { ServerSideDataSource } from '../../../shared/abstract-classes/serverSideDataSource';

import { EventsAllData, ListEventModel } from '../models/event.model';
import { EventsDataService } from './events-data.service';

export class EventsDataSourceService extends ServerSideDataSource<ListEventModel> {
  protected loadingSubject = new BehaviorSubject<boolean>(false);
  protected itemsSubject = new BehaviorSubject<ListEventModel[]>([]);

  private autocompleteSearch$ = new Subject<{
    search: string;
    existingFilters: AutocompleteOption[];
  }>();
  readonly autocompleteOptions$: Observable<GroupedAutocompleteOptions[]> = this.autocompleteSearch$
    .asObservable()
    .pipe(
      switchMap(({ search, existingFilters }) =>
        this.eventsDataService.getSuggestionsList(search).pipe(
          this.recoverFromError(),
          map(groupedOptions =>
            groupedOptions
              .map(group => {
                group.categories = group.categories.filter(
                  o => !existingFilters.find(f => f.category === o.category && f.value === o.value)
                );
                return group;
              })
              .filter(group => group.categories.length)
          )
        )
      )
    );
  private _filters$ = new Subject();
  private resolveLabelsSearch$ = new Subject<string>();

  readonly allAutocompleteOptions$: Observable<GroupedAutocompleteOptions[]> =
    this.resolveLabelsSearch$.asObservable().pipe(
      switchMap(search =>
        this.eventsDataService.getSuggestionsList(search).pipe(
          this.recoverFromError(),
          map(groupedOptions => groupedOptions.filter(group => group.categories.length))
        )
      ),
      shareReplay(1)
    );

  selection = new SelectionModel<ListEventModel>(true, []);
  loading$ = this.loadingSubject.asObservable();
  data: ListEventModel[] = [];
  totalElements = 0;
  isOnInexistentPage: boolean;

  constructor(
    protected eventsDataService: EventsDataService,
    public sort: MatSort,
    public paginator: MatPaginator
  ) {
    super(sort, paginator);

    this._filters$
      .pipe(
        switchMap(params =>
          this.eventsDataService.getListData(params).pipe(this.recoverFromError())
        ),
        tap(({ events: { totalElements, number, totalPages } }: EventsAllData) => {
          this.totalElements = totalElements;
          this.isOnInexistentPage = number ? number + 1 > totalPages : false;
        }),
        map((entitiesAllData: EventsAllData) => entitiesAllData.events.content),
        finalize(() => this.loadingSubject.next(false))
      )
      .subscribe((entities: ListEventModel[]) => {
        this.setData(entities);
      });
  }

  getAll(): void {
    this.selection.clear();
    this.loadingSubject.next(true);

    const params = {
      sort: `${this.sort.active || 'date_time_start'}:${this.sort.direction || 'desc'}`,
      size: this.paginator.pageSize || this.PAGE_SIZE,
      page: this.paginator.pageIndex || 0,
      ...this.filters,
    };

    this._filters$.next(params);
  }

  loadAutocompleteOptions(search: string, existingFilters: AutocompleteOption[] = []): void {
    this.autocompleteSearch$.next({ search, existingFilters });
  }

  loadAllAutocompleteOptions(search: string = ''): void {
    this.resolveLabelsSearch$.next(search);
  }
}
